import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserHistory, deleteHistoryItem } from '../services/historyService';
import { getUserFolders, getDefaultFolder, createFolder, deleteFolder } from '../services/folderService';
import { moveHistoryItemToFolder } from '../services/historyService';
import { useAuth } from '../contexts/AuthContext';
import { useSearch } from '../contexts/SearchContext';
import VideoGrid from '../components/VideoGrid';
import { Plus, Trash2 } from 'react-feather';
import { isValidYouTubeInput } from '../utils/youtubeUtils';

const HistoryPage = () => {
  const { currentUser } = useAuth();
  const { searchQuery } = useSearch();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('all-videos');
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [error, setError] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [showMoveDialog, setShowMoveDialog] = useState(false);

  useEffect(() => {
    if (currentUser) {
      loadHistory();
      loadFolders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const loadHistory = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      const userHistory = await getUserHistory(currentUser.uid, 100);
      setHistory(Array.isArray(userHistory) ? userHistory : []);
    } catch (err) {
      console.error('Failed to load history:', err);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFolders = async () => {
    if (!currentUser) return;
    
    try {
      const userFolders = await getUserFolders(currentUser.uid);
      setFolders(userFolders || []);
    } catch (err) {
      console.error('Failed to load folders:', err);
      setFolders([]);
    }
  };

  const handleAnalyze = async () => {
    if (!videoUrl.trim() || !isValidYouTubeInput(videoUrl.trim())) {
      setError('Please enter a valid YouTube URL');
      return;
    }

    const url = videoUrl.trim();
    const { extractVideoId } = await import('../utils/youtubeUtils');
    const videoId = extractVideoId(url);
    
    if (videoId) {
      // Navigate directly to video analysis page - it will handle analysis if needed
      navigate(`/video/${videoId}`);
    } else {
      setError('Could not extract video ID from URL');
    }
  };

  const handleDelete = async (videoId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this video?')) {
      return;
    }

    try {
      await deleteHistoryItem(videoId, currentUser?.uid);
      setHistory(history.filter(v => v.id !== videoId));
      setSelectedVideos(selectedVideos.filter(id => id !== videoId));
      setError(null);
    } catch (err) {
      console.error('Error deleting video:', err);
      setError(err.message || 'Failed to delete video');
    }
  };

  const handleToggleFavorite = async (videoId, currentFavoriteStatus, e) => {
    e.stopPropagation();
    // Favorites functionality will be on Favourites page
    // This can be empty or show a message
  };

  const handleSelectVideo = (videoId, e) => {
    e.stopPropagation();
    setSelectedVideos(prev => 
      prev.includes(videoId) 
        ? prev.filter(id => id !== videoId)
        : [...prev, videoId]
    );
  };

  const handleSelectAll = () => {
    if (selectedVideos.length === filteredHistory.length) {
      setSelectedVideos([]);
    } else {
      setSelectedVideos(filteredHistory.map(v => v.id));
    }
  };

  const handleMoveSelected = async (targetFolderId) => {
    if (selectedVideos.length === 0) return;

    const folderId = targetFolderId === 'all-videos' ? null : targetFolderId;
    
    try {
      await Promise.all(
        selectedVideos.map(videoId => moveHistoryItemToFolder(videoId, folderId))
      );
      
      setHistory(history.map(video =>
        selectedVideos.includes(video.id) ? { ...video, folderId } : video
      ));
      
      setSelectedVideos([]);
      setShowMoveDialog(false);
      setError(null);
    } catch (err) {
      console.error('Error moving videos:', err);
      setError(err.message || 'Failed to move videos');
    }
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim()) {
      setError('Please enter a folder name');
      return;
    }

    if (!currentUser?.uid) {
      setError('You must be logged in to create a folder');
      return;
    }

    try {
      await createFolder(currentUser.uid, newFolderName.trim());
      setNewFolderName('');
      setShowCreateFolder(false);
      setError(null);
      await loadFolders();
    } catch (err) {
      console.error('Error creating folder:', err);
      setError(err.message || 'Failed to create folder');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Videos will be moved to "All".')) {
      return;
    }

    try {
      await deleteFolder(folderId, currentUser?.uid);
      await loadFolders();
      if (selectedFolder === folderId) {
        setSelectedFolder('all-videos');
      }
      setError(null);
    } catch (err) {
      console.error('Error deleting folder:', err);
      setError(err.message || 'Failed to delete folder');
    }
  };

  const filteredHistory = useMemo(() => {
    let filtered = selectedFolder === 'all-videos' 
      ? history 
      : history.filter(item => item.folderId === selectedFolder);

    // Filter by search query if provided
    if (searchQuery && searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(item => {
        const videoTitle = (item.videoTitle || item.customName || '').toLowerCase();
        const channelName = (item.channelName || '').toLowerCase();
        return videoTitle.includes(query) || channelName.includes(query);
      });
    }

    return filtered;
  }, [history, selectedFolder, searchQuery]);

  const defaultFolder = getDefaultFolder();
  const allFolders = [defaultFolder, ...folders];

  return (
    <div style={{
      fontFamily: "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
      background: 'linear-gradient(135deg, #1a1b2e 0%, #2d1b3d 100%)',
      color: '#ffffff',
      minHeight: '100vh',
      padding: '30px',
    }}>
      {/* Top Bar - URL Input and Analyze Button */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '32px',
      }}>
        <input
          type="text"
          value={videoUrl}
          onChange={(e) => setVideoUrl(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAnalyze()}
          placeholder="Enter YouTube video URL"
          style={{
            flex: 1,
            padding: '14px 16px',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '8px',
            color: 'white',
            fontSize: '14px',
            fontStyle: 'normal',
            transition: 'all 0.2s',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = 'rgba(124, 58, 237, 0.5)';
            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
          }}
        />
        <button
          onClick={handleAnalyze}
          style={{
            padding: '14px 28px',
            backgroundColor: '#5432FF',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#4328cc'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#5432FF'}
        >
          Analyze Comments
        </button>
      </div>

      {/* Content Header - Filters and New Folder Button */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '24px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
        }}>
          {allFolders.map(folder => (
            <button
              key={folder.id}
              onClick={() => setSelectedFolder(folder.id)}
              style={{
                padding: '8px 16px',
                backgroundColor: selectedFolder === folder.id ? '#6C5CE7' : 'transparent',
                border: 'none',
            borderRadius: '8px',
            color: selectedFolder === folder.id ? 'white' : '#6C5CE7',
            fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              {folder.name === 'All Videos' ? 'All' : folder.name} ({folder.id === 'all-videos' ? history.length : history.filter(h => h.folderId === folder.id).length})
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowCreateFolder(true)}
          className="new-folder-button"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px', // py-2.5 px-5 (secondary button padding)
          }}
        >
          <Plus size={16} />
          <span>New Folder</span>
        </button>
      </div>

      {/* Create Folder Form */}
      {showCreateFolder && (
        <div style={{
          padding: '16px',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
          marginBottom: '24px',
        }}>
          <form onSubmit={handleCreateFolder} style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
          }}>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name"
              autoFocus
              maxLength={50}
              style={{
                flex: 1,
                padding: '10px 12px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '6px',
                color: 'white',
                fontSize: '14px',
                fontStyle: 'normal',
              }}
            />
            <button
              type="submit"
              style={{
                padding: '10px 20px',
                backgroundColor: '#6C5CE7',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Create
            </button>
            <button
              type="button"
              onClick={() => { setShowCreateFolder(false); setNewFolderName(''); setError(null); }}
              style={{
                padding: '10px 20px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '12px 16px', // spacing: 3 (12px) and 4 (16px)
          backgroundColor: '#fee2e2', // error.light
          border: '1px solid #ef4444', // error.base
          borderRadius: '8px', // borderRadius.md
          color: '#991b1b', // error.dark
          marginBottom: '24px', // spacing.6
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#ffcdd2',
              cursor: 'pointer',
              fontSize: '18px',
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Select All */}
      {filteredHistory.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '20px',
        }}>
          <input
            type="checkbox"
            checked={selectedVideos.length === filteredHistory.length && filteredHistory.length > 0}
            onChange={handleSelectAll}
            className="custom-checkbox"
          />
          <label style={{
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.7)',
            cursor: 'pointer',
          }}>
            Select All ({filteredHistory.length})
          </label>
        </div>
      )}

      {/* Selection Actions Bar */}
      {selectedVideos.length > 0 && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: 'rgba(108, 92, 231, 0.2)',
          borderRadius: '8px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <span style={{
              fontSize: '14px',
              fontWeight: 500,
              color: 'white',
            }}>
              {selectedVideos.length} video{selectedVideos.length !== 1 ? 's' : ''} selected
            </span>
            <button
              onClick={() => setShowMoveDialog(true)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6C5CE7',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Move to Folder
            </button>
            <button
              onClick={() => {
                if (window.confirm(`Delete ${selectedVideos.length} video${selectedVideos.length !== 1 ? 's' : ''}?`)) {
                  Promise.all(selectedVideos.map(id => deleteHistoryItem(id, currentUser?.uid)))
                    .then(() => {
                      setHistory(history.filter(v => !selectedVideos.includes(v.id)));
                      setSelectedVideos([]);
                      setError(null);
                    })
                    .catch(err => {
                      console.error('Error deleting videos:', err);
                      setError('Failed to delete some videos');
                    });
                }
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#ef4444', // error.base
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Delete Selected
            </button>
          </div>
          <button
            onClick={() => setSelectedVideos([])}
            style={{
              padding: '8px 16px',
              backgroundColor: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '6px',
              color: 'white',
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            Clear Selection
          </button>
        </div>
      )}

      {/* Move Dialog */}
      {showMoveDialog && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50,
          }}
          onClick={() => setShowMoveDialog(false)}
        >
          <div
            style={{
              backgroundColor: '#1a1a2e',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              fontSize: '18px',
              fontWeight: 600,
              marginBottom: '16px',
              color: 'white',
            }}>
              Move to Folder
            </h3>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}>
              {allFolders.map(folder => (
                <button
                  key={folder.id}
                  onClick={() => handleMoveSelected(folder.id)}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '12px 16px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.05)'}
                >
                  {folder.name === 'All Videos' ? 'All' : folder.name}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowMoveDialog(false)}
              style={{
                marginTop: '16px',
                width: '100%',
                padding: '10px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Video Grid */}
      {loading ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '64px',
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid rgba(255, 255, 255, 0.2)',
            borderTopColor: '#6C5CE7',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}></div>
          <p style={{
            marginTop: '16px',
            color: 'rgba(255, 255, 255, 0.7)',
          }}>
            Loading videos...
          </p>
        </div>
      ) : filteredHistory.length === 0 ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '64px',
          textAlign: 'center',
        }}>
          <p style={{
            fontSize: '16px',
            color: 'rgba(255, 255, 255, 0.7)',
            marginBottom: '8px',
          }}>
            {history.length === 0 
              ? 'No videos yet.'
              : selectedFolder === 'all-videos'
              ? 'No videos yet.'
              : 'No videos in this folder.'}
          </p>
          <p style={{
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.5)',
          }}>
            {history.length === 0 
              ? 'Analyze a YouTube video to see it here!'
              : 'Try selecting a different folder or analyze a new video.'}
          </p>
        </div>
      ) : (
        <VideoGrid
          videos={filteredHistory}
          onToggleFavorite={handleToggleFavorite}
          onDelete={handleDelete}
          onSelectVideo={handleSelectVideo}
          selectedVideos={selectedVideos}
          showCheckbox={true}
          showFavorite={false}
          showDelete={true}
          canFavorite={false}
        />
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default HistoryPage;
